import React, { useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { Box, Loader, TextButton } from '@wix/design-system';
import { ChevronLeft } from '@wix/wix-ui-icons-common/odeditor';
import { WIX_STUDIO_HEADER_HEIGHT } from '@wix/core-domain-search';
import { DomainSearchSuiteModule, EntriModule, mountEntriContainer, unmountEntriContainer, waitForEntriIframeReady } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { EntriFlowErrorType, EntriFlowStatus } from '@wix/domains-serverless-interface';
import { usePremiumEssentialsContext } from '@wix/premium-fed-sdk/@premium-essentials';
import { ENTRI_ENABLED_BY_SPEC } from './connectEntriEligibility';
import { useQueryParams } from '../../hooks/useQueryParams';
export const ConnectEntriRoute = _ref => {
  let onBack = _ref.onBack,
    onConnectEntri = _ref.onConnectEntri,
    handlers = _ref.handlers,
    _ref$externalViewer = _ref.externalViewer,
    externalViewer = _ref$externalViewer === void 0 ? false : _ref$externalViewer;
  const _useQueryParams = useQueryParams(),
    getQueryParam = _useQueryParams.getQueryParam;
  const domainName = getQueryParam('domainName') ?? undefined;
  const metaSiteId = getQueryParam('metaSiteId') ?? undefined;
  const siteName = getQueryParam('siteName') ?? undefined;
  const takenInAnotherAccount = getQueryParam('takenInAnotherAccount') === 'true';
  if (!domainName || !metaSiteId) {
    return /*#__PURE__*/React.createElement(Redirect, {
      to: "/"
    });
  }
  return /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    }
  }, /*#__PURE__*/React.createElement(ConnectEntriContent, {
    domainName: domainName,
    metaSiteId: metaSiteId,
    siteName: siteName,
    takenInAnotherAccount: takenInAnotherAccount,
    onBack: onBack,
    onConnectEntri: onConnectEntri,
    handlers: handlers,
    externalViewer: externalViewer
  }));
};
const ConnectEntriContent = _ref2 => {
  let domainName = _ref2.domainName,
    metaSiteId = _ref2.metaSiteId,
    siteName = _ref2.siteName,
    takenInAnotherAccount = _ref2.takenInAnotherAccount,
    onBack = _ref2.onBack,
    onConnectEntri = _ref2.onConnectEntri,
    handlers = _ref2.handlers,
    externalViewer = _ref2.externalViewer;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.internals.useTranslations(),
    t = _DomainSearchSuiteMod.t;
  const _usePremiumEssentials = usePremiumEssentialsContext(),
    httpClient = _usePremiumEssentials.httpClient;
  const hasStartedRef = useRef(false);
  const hasFlowEndedRef = useRef(false);
  const containerRef = useRef(null);
  const _useState = useState(false),
    isEntriIframeLoaded = _useState[0],
    setIsEntriIframeLoaded = _useState[1];
  const flowRef = useRef({
    domainName,
    metaSiteId,
    siteName,
    takenInAnotherAccount,
    onBack,
    onConnectEntri,
    handlers,
    httpClient,
    t
  });
  flowRef.current = {
    domainName,
    metaSiteId,
    siteName,
    takenInAnotherAccount,
    onBack,
    onConnectEntri,
    handlers,
    httpClient,
    t
  };
  const _EntriModule$internal = EntriModule.internals.domainsEntriModalsController,
    stopConnectionModal = _EntriModule$internal.stopConnectionModal,
    domainTakenInWixEntriModal = _EntriModule$internal.domainTakenInWixEntriModal;
  const isExitingRef = useRef(false);
  const exitFlow = async function (_temp) {
    let _ref3 = _temp === void 0 ? {} : _temp,
      _ref3$hideModal = _ref3.hideModal,
      hideModal = _ref3$hideModal === void 0 ? false : _ref3$hideModal;
    if (isExitingRef.current) {
      return;
    }
    isExitingRef.current = true;
    await EntriModule.SDK.closeEntriFlow();
    if (hideModal) {
      stopConnectionModal.hide == null || stopConnectionModal.hide();
    }
    onBack == null || onBack({
      domainName,
      metaSiteId,
      siteName,
      takenInAnotherAccount
    });
  };
  const showStopConnectionModal = function (isStopping) {
    if (isStopping === void 0) {
      isStopping = false;
    }
    stopConnectionModal.show({
      isStopping,
      onStop: () => {
        showStopConnectionModal(true);
        void exitFlow({
          hideModal: true
        });
      },
      onContinue: () => stopConnectionModal.hide == null ? void 0 : stopConnectionModal.hide()
    });
  };

  // Zero-arg wrapper: passing `showStopConnectionModal` straight to a handler
  // would feed it the event as `isStopping`.
  const handleBack = () => showStopConnectionModal();

  // Read through a ref so the Back-gesture guard below mounts once and never
  // re-pushes a history entry when the parent re-renders with a new onBack.
  const handleBackRef = useRef(handleBack);
  handleBackRef.current = handleBack;

  // Guard the phone/browser Back gesture: keep a sentinel entry on the stack so
  // Back lands back here and asks for confirmation, then closes Entri properly,
  // instead of the route being ripped out mid-flow.
  useEffect(() => {
    window.history.pushState({
      entriGuard: true
    }, '');
    const onPopState = () => {
      window.history.pushState({
        entriGuard: true
      }, '');
      handleBackRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  useEffect(() => {
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    if (containerRef.current) {
      // A previous visit in this page session can leave a container behind, and
      // `mountEntriContainer` no-ops when one already exists — Entri's embed
      // would then render into that stale node instead of this route's box.
      unmountEntriContainer();
      mountEntriContainer({
        parent: containerRef.current,
        cssText: 'flex:1 1 auto; width: 100%;'
      });
    }
    let disposed = false;
    const entriReady = waitForEntriIframeReady();
    void entriReady.then(() => {
      if (!disposed) {
        setIsEntriIframeLoaded(true);
      }
    });
    const runEntri = async () => {
      var _flow$handlers;
      const flow = flowRef.current;

      // A domain registered in another Wix account is not eligible under the
      // plain `entriConfig` read, so the flow has to start from the signed
      // taken-in-another-account eligibility instead.
      const overrideEntriEligibility = flow.takenInAnotherAccount ? await DomainsApiClient.query.domainTakenInAnotherAccountEntriEligibility({
        httpClient: flow.httpClient,
        request: {
          domain: flow.domainName
        }
      }) : undefined;
      const result = await EntriModule.SDK.startEntriFlow({
        domain: flow.domainName,
        siteName: flow.siteName ?? '',
        metaSiteId: flow.metaSiteId,
        skipIntro: true,
        fullPage: true,
        useCustomExitConfirmation: true,
        enabledBySpec: ENTRI_ENABLED_BY_SPEC,
        overrideEntriEligibility,
        t: flow.t
      });
      hasFlowEndedRef.current = true;
      if (result.status === EntriFlowStatus.manual && result.errorType !== EntriFlowErrorType.setupFailed && flow.takenInAnotherAccount) {
        const args = {
          domainName: flow.domainName,
          metaSiteId: flow.metaSiteId,
          siteName: flow.siteName,
          takenInAnotherAccount: flow.takenInAnotherAccount
        };

        // Entri unmounts the embed container on close, so this route has nothing
        // left to show: leave for the intro page first, then put the modal over
        // it. Restarting in place would also re-create the container through
        // `mountEntriContainer()` with no parent — a fixed full-screen overlay
        // covering this route's own Back link.
        flow.onBack == null || flow.onBack(args);
        domainTakenInWixEntriModal.show({
          connectHandler: async () => {
            domainTakenInWixEntriModal.hide();
            await (flow.onConnectEntri == null ? void 0 : flow.onConnectEntri(args));
          }
        });
        return;
      }
      await ((_flow$handlers = flow.handlers) == null || _flow$handlers.connect == null ? void 0 : _flow$handlers.connect({
        result: result.status,
        errorType: result.errorType,
        domainName: flow.domainName,
        msid: flow.metaSiteId,
        siteName: flow.siteName,
        externalViewer
      }));
    };
    void runEntri();
    return () => {
      disposed = true;
      // Only tear down a flow that is still running. Once Entri resolved it has
      // already closed itself and unmounted the container, and calling close()
      // on the closed widget leaves it unable to render on the next visit —
      // blank page, no errors, cleared only by a refresh.
      if (!hasFlowEndedRef.current) {
        void EntriModule.SDK.closeEntriFlow();
      }
      unmountEntriContainer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    height: `calc(100dvh - ${WIX_STUDIO_HEADER_HEIGHT})`
  }, /*#__PURE__*/React.createElement(Box, {
    padding: "SP3",
    flex: "0 0 auto"
  }, /*#__PURE__*/React.createElement(TextButton, {
    skin: "dark",
    prefixIcon: /*#__PURE__*/React.createElement(ChevronLeft, null),
    onClick: handleBack
  }, t('entri.intro_modal.back_cta'))), /*#__PURE__*/React.createElement(Box, {
    ref: containerRef,
    direction: "vertical",
    flex: "1 1 auto",
    align: "center",
    verticalAlign: "middle"
  }, !isEntriIframeLoaded && /*#__PURE__*/React.createElement(Loader, null)));
};
//# sourceMappingURL=ConnectEntriRoute.js.map